import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UnitsCard, PropertyCardData } from '../../../../shared/components/units-card/units-card';
import { ApiProperty, PaginationInfo } from '../../../../core/models/api-property.model';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { PropertyService } from '../../services/property.service';
import { LanguageService } from '../../../../core/services/language.service';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';

@Component({
  selector: 'app-all-properties',
  imports: [CommonModule, FormsModule, UnitsCard, TranslatePipe],
  templateUrl: './all-properties.html',
  standalone: true,
  styleUrl: './all-properties.scss',
})
export class AllProperties implements OnInit, OnDestroy {
  /* ===============================
   UI State
  =============================== */
  isFilterSidebarOpen = true;
  viewMode: 'grid' | 'map' = 'grid';
  sortBy = 'recommended';
  showFilters = false;
  isLoading = false;
  errorMessage = '';
  searchQuery = '';
  activePurpose: 'all' | 'rent' | 'sale' = 'all';
  activePropertyType = 'all';
  activeCity = 'all';
  activeBedrooms = 'any';
  minPrice: number | null = null;
  maxPrice: number | null = null;
  minArea: number | null = null;
  maxArea: number | null = null;

  /* ===============================
   Data
  =============================== */
  properties: ApiProperty[] = [];
  private allProperties: ApiProperty[] = [];
  paginationInfo: PaginationInfo | null = null;
  totalResults = 0;
  currentPage = 1;
  itemsPerPage = 10;
  private languageSubscription: Subscription | null = null;
  private routeSubscription: Subscription | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private propertyService: PropertyService,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    // Listen to query params for search and purpose
    this.routeSubscription = this.route.queryParams.subscribe((params) => {
      this.searchQuery = params['title'] || params['search'] || '';
      const purposeParam = params['purpose'];
      if (purposeParam === 'rent' || purposeParam === 'sale') {
        this.activePurpose = purposeParam;
      } else {
        this.activePurpose = 'all';
      }
      this.loadProperties();
    });

    // الاشتراك في تغييرات اللغة لإعادة تحميل البيانات
    this.languageSubscription = this.languageService.currentLanguage$
      .pipe(skip(1))
      .subscribe(() => {
        this.loadProperties(this.currentPage);
      });
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
  }

  loadProperties(page: number = 1): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.currentPage = page;

    const purposeParam = this.activePurpose !== 'all' ? this.activePurpose : undefined;

    this.propertyService
      .getProperties(page, this.itemsPerPage, this.searchQuery, purposeParam)
      .subscribe({
        next: (response) => {
          console.log('📦 Properties Response:', response);
          console.log('🔍 Search Query:', this.searchQuery);
          console.log('📊 Data:', response.results.data);

          this.allProperties = response.results.data as unknown as ApiProperty[];
          this.applyClientFilters();
          this.paginationInfo = {
            data: response.results.data as unknown as ApiProperty[],
            pages: response.results.pages,
            currentPage:
              typeof response.results.currentPage === 'string'
                ? parseInt(response.results.currentPage)
                : response.results.currentPage,
            totalItems: response.results.totalItems,
            itemsPerPage: response.results.itemsPerPage,
            nextPage: response.results.nextPage,
            previousPage: response.results.previousPage,
          };
          this.isLoading = false;

          console.log('✅ Properties loaded:', this.properties.length);
        },
        error: (error) => {
          console.error('❌ Error loading properties:', error);
          this.errorMessage = 'حدث خطأ أثناء تحميل العقارات';
          this.isLoading = false;
        },
      });
  }

  loadMore(): void {
    if (this.paginationInfo?.nextPage) {
      this.loadProperties(this.paginationInfo.nextPage);
    }
  }

  toggleFilterSidebar(): void {
    this.isFilterSidebarOpen = !this.isFilterSidebarOpen;
  }

  changeView(mode: 'grid' | 'map'): void {
    this.viewMode = mode;
  }

  changeSort(value: string): void {
    this.sortBy = value;
    this.applyClientFilters();
  }

  applyFilters(): void {
    this.applyClientFilters();
  }

  resetFilters(): void {
    this.activePropertyType = 'all';
    this.activeCity = 'all';
    this.activeBedrooms = 'any';
    this.minPrice = null;
    this.maxPrice = null;
    this.minArea = null;
    this.maxArea = null;
    this.sortBy = 'recommended';
    
    // Clear URL query parameters to reset search query and purpose
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
    
    this.applyClientFilters();
  }

  viewPropertyDetails(property: ApiProperty): void {
    console.log('Property details:', property);
  }

  onPropertyClick(property: PropertyCardData): void {
    console.log('🔍 Property clicked:', property);

    // Check if this is a search result with targetId and targetType
    if ('targetId' in property && 'targetType' in property) {
      const apiProperty = property as ApiProperty;
      const targetId = apiProperty.targetId;
      const targetType = apiProperty.targetType;

      console.log('📍 Target ID:', targetId);
      console.log('📍 Target Type:', targetType);

      // Navigate based on targetType
      if (targetType === 'property' || targetType === 'project') {
        // Navigate to property details
        this.router.navigate(['/property', targetId], {
          state: { property: property },
        });
      } else if (targetType === 'unit') {
        // Check priceType to determine if it's rent or sale
        const priceType = apiProperty.priceType;
        const purpose = apiProperty.purpose;

        console.log('💰 Price Type:', priceType);
        console.log('🎯 Purpose:', purpose);

        // Determine route based on purpose and priceType
        if (purpose === 'sale_and_rent') {
          // Check priceType to decide
          if (priceType === 'daily' || priceType === 'monthly') {
            this.router.navigate(['/rent', targetId], {
              state: { property: property },
            });
          } else if (priceType === 'total') {
            this.router.navigate(['/buy', targetId], {
              state: { property: property },
            });
          }
        } else if (purpose === 'rent') {
          this.router.navigate(['/rent', targetId], {
            state: { property: property },
          });
        } else if (purpose === 'sale') {
          this.router.navigate(['/buy', targetId], {
            state: { property: property },
          });
        }
      }
    } else {
      // Fallback to old behavior
      const id = '_id' in property ? property._id : property.id;
      this.router.navigate(['/property', id], {
        state: { property: property },
      });
    }
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  setPurpose(purpose: 'all' | 'rent' | 'sale'): void {
    this.activePurpose = purpose;
    this.loadProperties(1);
  }

  private applyClientFilters(): void {
    let filtered = [...this.allProperties];

    if (this.activePurpose !== 'all') {
      filtered = filtered.filter((property) => {
        const purpose = property.purpose;
        if (!purpose) {
          return this.activePurpose === 'sale';
        }
        if (purpose === 'sale_and_rent') {
          return true;
        }
        return purpose === this.activePurpose;
      });
    }

    if (this.activePropertyType !== 'all') {
      filtered = filtered.filter((property) => this.matchesType(property, this.activePropertyType));
    }

    if (this.activeCity !== 'all') {
      filtered = filtered.filter((property) => this.getCity(property) === this.activeCity);
    }

    if (this.activeBedrooms !== 'any') {
      const bedrooms = Number(this.activeBedrooms);
      filtered = filtered.filter((property) => {
        const value = Number(property.bedrooms ?? 0);
        return bedrooms === 4 ? value >= 4 : value === bedrooms;
      });
    }

    if (this.minPrice !== null && this.minPrice !== undefined) {
      filtered = filtered.filter((property) => this.getPrice(property) >= this.minPrice!);
    }

    if (this.maxPrice !== null && this.maxPrice !== undefined) {
      filtered = filtered.filter((property) => this.getPrice(property) <= this.maxPrice!);
    }

    if (this.minArea !== null && this.minArea !== undefined) {
      filtered = filtered.filter((property) => this.getArea(property) >= this.minArea!);
    }

    if (this.maxArea !== null && this.maxArea !== undefined) {
      filtered = filtered.filter((property) => this.getArea(property) <= this.maxArea!);
    }

    filtered = this.applySort(filtered);
    this.properties = filtered;
    this.totalResults = filtered.length;
  }

  private applySort(list: ApiProperty[]): ApiProperty[] {
    const sorted = [...list];

    switch (this.sortBy) {
      case 'priceLow':
        return sorted.sort((a, b) => this.getPrice(a) - this.getPrice(b));
      case 'priceHigh':
        return sorted.sort((a, b) => this.getPrice(b) - this.getPrice(a));
      case 'areaLarge':
        return sorted.sort((a, b) => this.getArea(b) - this.getArea(a));
      case 'newest':
        return sorted.sort(
          (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
        );
      default:
        return sorted;
    }
  }

  private matchesType(property: ApiProperty, selectedType: string): boolean {
    const type = (property.type || '').toLowerCase();
    return type.includes(selectedType.toLowerCase());
  }

  private getCity(property: ApiProperty): string {
    return (property.location?.city || '').toLowerCase();
  }

  private getPrice(property: ApiProperty): number {
    return Number(property.displayPrice ?? 0);
  }

  private getArea(property: ApiProperty): number {
    return Number(property.area ?? 0);
  }
}
